import React, { useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayoutOptimized';
import { generateRecommendations } from '../../lib/openaiService';

const TestEdgeFunctionPage: React.FC = () => {
  const [prompt, setPrompt] = useState<string>('Donne-moi 3 conseils marketing pour une boulangerie.');
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toISOString()}] ${message}`]);
  };

  // Test via le service openaiService avec fallback automatique
  const testViaService = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      addLog('Démarrage du test via le service sécurisé...');
      
      // Appel du service qui gère le fallback automatiquement
      const recommendations = await generateRecommendations(prompt, {
        model: 'gpt-4o-mini',
        temperature: 0.7,
        max_tokens: 500
      });
      
      addLog('Réponse reçue via le service sécurisé.');
      setResponse({ recommendations });
    } catch (err: any) {
      console.error('Erreur lors du test via service:', err);
      addLog(`Erreur: ${err.message || 'Erreur inconnue'}`);
      setError('Impossible de générer des recommandations. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <h1 className="text-2xl font-bold mb-6">Test des recommandations IA</h1>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Entrez votre demande :
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            rows={3}
            disabled={loading}
          />
        </div>
        
        <div className="flex space-x-4 mb-6">
          <button
            onClick={testViaService}
            disabled={loading}
            className={`px-4 py-2 rounded-md text-white ${loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {loading ? 'Traitement...' : 'Tester la génération'}
          </button>
          <button
            onClick={clearLogs}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Effacer les logs
          </button>
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            <p className="font-bold">Erreur</p>
            <p>{error}</p>
          </div>
        )}
        
        {response && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">Réponse :</h2>
            <div className="p-4 bg-gray-50 rounded-md border border-gray-200 whitespace-pre-wrap">
              {typeof response.recommendations === 'string' 
                ? response.recommendations 
                : JSON.stringify(response.recommendations, null, 2)}
            </div>
          </div>
        )}
        
        <div className="mt-8">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold">Logs :</h2>
            <span className="text-sm text-gray-500">{logs.length} entrées</span>
          </div>
          <div className="bg-black text-green-400 p-4 rounded-md font-mono text-sm h-64 overflow-y-auto">
            {logs.length > 0 ? (
              logs.map((log, index) => (
                <div key={index} className="mb-1 border-b border-gray-800 pb-1">
                  {log}
                </div>
              ))
            ) : (
              <p className="text-gray-500">Aucun log à afficher</p>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default TestEdgeFunctionPage;
